import { namespace } from '@common-config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './calendar-grid.module.scss';
import { REFUSAL_KEY } from './event-chip';
import { buildChipViewModel } from './layout/chip-view-model';
import { canDragEvent } from './layout/drag-model';
import { planOccurrenceDelete } from './layout/delete-occurrence';
import { chipThumbnailSrc } from './layout/chip-thumbnail';
import { useAssetUrl } from '@common/services/asset-url-resolver';
import DestinationAvatars, { DestinationMemberList } from '@common/components/destination-avatars/destination-avatars';
import {
    campaignRecipientCount,
    messageDestination,
    type DestinationPlan,
} from '@common/components/destination-avatars/destination-plan';
import { useAudienceMemberHints, useCampaignDestination } from '@common/components/destination-avatars/use-campaign-destination';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import { campaignsSelectOneObjectByQuery, userMessagesSelectOneObjectByQuery } from '@common/reducers/DBServiceThunks';
import { describeSendFailure, isNotOnWhatsAppFailure } from '@common-components/message-status/message-status-config';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { buildRecurrenceSentence } from './recurrence-sentence';
import { destinationName } from './layout/destination-name';
import type { CalendarLeanEvent } from './types';

const CARD_WIDTH = 320;
const CARD_MAX_HEIGHT = 340;
const GAP = 10;

export interface HoverTarget {
    event: CalendarLeanEvent;
    rect: { top: number; left: number; right: number; bottom: number };
}

interface Props {
    target: HoverTarget;
    now: number;
}

// 'unconfirmed' shares the sent styling and the sent label: it went out, we are
// only missing the receipt.
//
// A send that has not gone out yet is "Scheduled" here, the word the sidebar row
// for the SAME send already uses — it used to say "Pending", so one message was
// pending in the hover card, scheduled in the list and waiting in the day panel.
const STATUS_KEY: Record<string, string> = {
    sent: 'campaign.status.sent',
    unconfirmed: 'campaign.status.sent',
    pending: 'calendar.sidebar.status.scheduled',
    failed: 'messageStatus.failed.label',
};

/**
 * WHO the hovered send goes to, from the same documents and the same plan the
 * sidebar row draws — so a campaign shows the same five faces and "+N" in both
 * places, through the same hook (and the same shared preview cache). The lean
 * calendar event carries only the first recipient, so the full document is read
 * from the store by id; if it is not there the lean event is used as it stands.
 *
 * A projected follow-up step has a synthetic id (`<drip>:step:<n>`) and targets
 * its parent's audience, so it is looked up through the parent. A step projected
 * straight from a drip document has neither in the store; it keeps its audience
 * NAME and simply draws no faces.
 */
function selectEventCampaign(state: CommonRootState, event: CalendarLeanEvent): any {
    if (!event.isCampaign) return undefined;
    const reducer = (state as any).DBCampaignReducer;
    return campaignsSelectOneObjectByQuery(reducer, { _id: event._id })
        ?? (event.parentCampaignId ? campaignsSelectOneObjectByQuery(reducer, { _id: event.parentCampaignId }) : undefined);
}

function selectMessageDestination(state: CommonRootState, event: CalendarLeanEvent): DestinationPlan {
    if (event.isCampaign) return NO_DESTINATION;
    const doc = userMessagesSelectOneObjectByQuery((state as any).DBUserMessageReducer, { _id: event._id });
    return messageDestination((doc as any) ?? event);
}

const NO_DESTINATION: DestinationPlan = { mode: 'none' };

/**
 * What a chip cannot show: the message itself.
 *
 * A chip is one line of a truncated title, which is enough to find something
 * and not enough to recognise it. The hover card shows the actual content —
 * text, media, who it goes to, and the cadence for a recurring send — without
 * making the user open an editor to look.
 */
export const ChipHoverCard: React.FC<Props> = ({ target, now }) => {
    const { t, i18n } = useTranslation(namespace);
    const { event, rect } = target;
    const vm = useMemo(() => buildChipViewModel(event, now), [event, now]);

    // Same CSP story as the chip's thumbnail (see event-chip.tsx): pointed at
    // the asset host directly, this preview is refused inside the extension and
    // the card silently loses its picture. Resolved through the worker it loads;
    // and if that fails, `mediaSrc` is undefined and the card simply has no
    // image — the rest of the card is unaffected.
    const [mediaFailed, setMediaFailed] = useState(false);
    useEffect(() => setMediaFailed(false), [vm.mediaUrl]);
    const { resolvedUrl: resolvedMedia, isLoading: mediaLoading } = useAssetUrl(vm.mediaUrl ?? '');
    const mediaSrc = chipThumbnailSrc({
        mediaUrl: vm.mediaUrl,
        resolvedUrl: resolvedMedia,
        isLoading: mediaLoading,
        imageFailed: mediaFailed,
    });
    // rrule's own toText() is English-only; this one speaks the UI language.
    const cadence = useMemo(
        () => (event.isRecurring ? buildRecurrenceSentence(event.rruleset, t) : ''),
        [event.isRecurring, event.rruleset, t],
    );

    // Prefer the side with room; fall back to flipping. Viewport coordinates,
    // so the card is positioned the same in the light DOM and in a shadow root.
    const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;

    const left = rect.right + GAP + CARD_WIDTH > viewportWidth
        ? Math.max(GAP, rect.left - GAP - CARD_WIDTH)
        : rect.right + GAP;
    const top = Math.min(
        Math.max(GAP, rect.top - 8),
        Math.max(GAP, viewportHeight - CARD_MAX_HEIGHT - GAP),
    );

    // For a non-campaign send, the full message doc carries the failure reason the lean
    // event does not — so a failed chip can say "not on WhatsApp" rather than a bare "Failed".
    const messageDoc = useAppSelector(state =>
        event.isCampaign
            ? undefined
            : (userMessagesSelectOneObjectByQuery((state as any).DBUserMessageReducer, { _id: event._id }) as UserMessage | undefined));
    const statusLabelKey = !event.isCampaign && vm.status === 'failed' && isNotOnWhatsAppFailure(messageDoc)
        ? 'messageStatus.failedNotOnWhatsApp.label'
        : (STATUS_KEY[vm.status] ?? 'calendar.sidebar.status.scheduled');

    const campaign = useAppSelector(state => selectEventCampaign(state, event));
    const taskSumByBoard = useAppSelector(getTaskSumByBoardIdRecord);
    const campaignPlan = useCampaignDestination(campaign ?? null, campaignRecipientCount(campaign, taskSumByBoard));
    const messagePlan = useAppSelector(state => selectMessageDestination(state, event));
    const destination = event.isCampaign ? campaignPlan : messagePlan;
    const campaignHints = useAudienceMemberHints(campaignPlan);
    const destinationHints = event.isCampaign ? campaignHints : undefined;

    // The faces show WHO; the name says WHICH list (or which person). A Status
    // is named in the reader's language, not by the English name stored at
    // compose time and never by the raw broadcast id.
    const audience = destinationName(event, vm.kind, t('scheduledMessage.status.myStatus'));

    // A chip that will not budge has to say so somewhere. It used to be in the
    // chip's native tooltip, which is gone (it double-painted over this card),
    // so the reason lives here now.
    const refusal = useMemo(() => canDragEvent(event, now).refusal, [event, now]);

    // There is nowhere on a month chip to put a delete button, so the card says
    // the gesture: point at the chip and press Delete (or right-click it). A series
    // also says how much goes — only this occurrence — which is the one case where
    // "just this one" is a distinction worth teaching. The card stays
    // `pointer-events: none`: this is a sentence, not a control. A destructive
    // action that vanishes when the mouse overshoots would be the opposite of
    // confident.
    const deletePlan = planOccurrenceDelete(event).kind;
    const deleteHintKey = deletePlan === 'scoped'
        ? 'calendar.grid.deleteOccurrenceHint'
        : deletePlan === 'single' ? 'calendar.grid.deleteKeyHint' : null;

    // Named the way the campaign screen names them ("First message", "Follow-up 1"),
    // not "Step 2 (follow-up)": the same message was called two different things
    // depending on which surface you were looking at, and the two even disagreed
    // about its number.
    const stepLabel = typeof event.dripStepIndex === 'number'
        ? (event.dripStepIndex === 0
            ? t('calendar.grid.firstMessage')
            : t('calendar.grid.followUpNumber', { number: event.dripStepIndex }))
        : null;

    return (
        <div className={styles.hoverCard} style={{ left, top, width: CARD_WIDTH }} role="tooltip">
            <div className={styles.hoverHead}>
                <span className={styles.hoverRail} style={{ background: vm.railColor }} />
                <div className={styles.hoverHeadText}>
                    <b>{formatInLanguage(event.dueDate, i18n.language, t('calendar.grid.hoverDateFormat'))}</b>
                    <small>
                        {event.isCampaign
                            ? t('calendar.campaign')
                            : vm.kind === 'status'
                                ? t('calendar.grid.statusKind')
                                : vm.kind === 'poll'
                                    ? t('btgpt.widgets.messageTypes.poll')
                                    : t('btgpt.widgets.messageTypes.message')}
                        {stepLabel ? ` · ${stepLabel}` : ''}
                    </small>
                </div>
                <span
                    className={[
                        styles.hoverStatus,
                        vm.status === 'sent' ? styles.hoverStatusSent
                            : vm.status === 'failed' ? styles.hoverStatusFailed
                                : styles.hoverStatusPending,
                    ].join(' ')}
                >
                    {t(statusLabelKey)}
                </span>
            </div>

            {/* WHY it failed — the same words the chat bubble shows, right under the status so a
                full card never clips it (the card is height-capped). A bare "Failed" left people
                guessing (e.g. their WhatsApp Web tab was closed). */}
            {!event.isCampaign && vm.status === 'failed' && (
                <div className={styles.hoverFailure}>{describeSendFailure(messageDoc, t)}</div>
            )}

            {mediaSrc && (
                <img
                    className={styles.hoverMedia}
                    src={mediaSrc}
                    alt=""
                    onError={() => setMediaFailed(true)}
                />
            )}

            <div className={styles.hoverBody}>
                {event.isPoll && event.poll?.question
                    ? event.poll.question
                    : (event.message || t('btgpt.widgets.noMessageContent'))}
            </div>

            {(audience || destination.mode !== 'none' || cadence || vm.isPaused) && (
                <div className={styles.hoverMeta}>
                    {(audience || destination.mode !== 'none') && (
                        <div className={styles.hoverTo} style={{ ['--dest-accent' as any]: vm.railColor }}>
                            <DestinationAvatars plan={destination} size="card" hints={destinationHints} />
                            {audience && <span className={styles.hoverToText}>{t('calendar.grid.to', { names: audience })}</span>}
                        </div>
                    )}
                    {/* WHO: the same list the sidebar row's popover shows — the
                        face above says one person, the name says which list, and
                        this says who is in it, capped with "and N more". */}
                    {destination.mode === 'group' && (
                        <div className={styles.hoverMembers}>
                            <DestinationMemberList plan={destination} hints={destinationHints} showHeading={false} />
                        </div>
                    )}
                    {cadence && <span>{t('calendar.grid.repeatsCadence', { cadence })}</span>}
                    {vm.isPaused && <span>{t('campaign.status.paused')}</span>}
                </div>
            )}

            {refusal && REFUSAL_KEY[refusal] && (
                <div className={styles.hoverRefusal}>{t(REFUSAL_KEY[refusal])}</div>
            )}

            {deleteHintKey && (
                <div className={styles.hoverHint}>{t(deleteHintKey)}</div>
            )}
        </div>
    );
};
